import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

// Theme definitions - 5 distinct premium designs with comprehensive styling
export const THEMES = {
  // Design 1: Classic CRM layout
  classic: {
    id: 'classic',
    name: 'Classic',
    nameHe: 'קלאסי',
    description: 'Clean, familiar CRM layout',
    descriptionHe: 'עיצוב CRM נקי ומוכר',
    icon: '☁️',

    // Color palette - Exact modern UI framework colors
    colors: {
      // legacy CRM brand blue
      primary: '#0176d3',
      primaryHover: '#014486',
      primaryLight: '#d8edff',
      primaryDark: '#032d60',

      // Neutral grays
      secondary: '#706e6b',
      secondaryHover: '#514f4d',

      // Accent colors
      accent: '#1b96ff',
      accentLight: '#d8edff',

      // Background colors - exact legacy CRM
      bgPrimary: '#f3f3f3',
      bgSecondary: '#ffffff',
      bgTertiary: '#fafaf9',
      bgHeader: '#032d60',
      bgSidebar: '#ffffff',
      bgCard: '#ffffff',
      bgHover: '#f3f3f3',
      bgSelected: '#d8edff',
      bgInput: '#ffffff',
      bgModal: '#ffffff',

      // Text colors - exact legacy CRM
      textPrimary: '#181818',
      textSecondary: '#3e3e3c',
      textTertiary: '#706e6b',
      textInverse: '#ffffff',
      textLink: '#0176d3',
      textMuted: '#969492',

      // Border colors - exact legacy CRM
      borderPrimary: '#c9c9c9',
      borderSecondary: '#dddbda',
      borderFocus: '#0176d3',
      borderInput: '#c9c9c9',

      // Status colors - exact legacy CRM
      success: '#2e844a',
      successLight: '#cdefc4',
      successDark: '#194e31',
      warning: '#dd7a01',
      warningLight: '#fef0cd',
      warningDark: '#8e4b02',
      error: '#ea001e',
      errorLight: '#feded8',
      errorDark: '#8e030f',
      info: '#0176d3',
      infoLight: '#d8edff',

      shadow: 'rgba(0, 0, 0, 0.16)',
      overlay: 'rgba(0, 0, 0, 0.6)',
      divider: '#dddbda',
    },

    // Typography - legacy CRM Sans
    typography: {
      fontFamily: "'Salesforce Sans', Arial, sans-serif",
      fontFamilyHeading: "'Salesforce Sans', Arial, sans-serif",
      fontFamilyMono: "Consolas, Menlo, Monaco, 'Courier New', monospace",

      // Exact legacy CRM font sizes
      fontSizeXs: '0.625rem',   // 10px
      fontSizeSm: '0.75rem',    // 12px
      fontSizeMd: '0.8125rem',  // 13px - legacy CRM default body
      fontSizeLg: '0.875rem',   // 14px
      fontSizeXl: '1rem',       // 16px
      fontSize2xl: '1.125rem',  // 18px
      fontSize3xl: '1.25rem',   // 20px

      fontWeightLight: '300',
      fontWeightNormal: '400',
      fontWeightMedium: '500',
      fontWeightSemibold: '700',
      fontWeightBold: '700',

      lineHeightTight: '1.25',
      lineHeightNormal: '1.5',
      lineHeightRelaxed: '1.75',

      letterSpacingTight: '0',
      letterSpacingNormal: '0',
      letterSpacingWide: '0.0125em',
    },

    // Spacing system - legacy CRM 0.25rem base
    spacing: {
      unit: '0.25rem',
      xxs: '0.125rem',   // 2px
      xs: '0.25rem',     // 4px
      sm: '0.5rem',      // 8px
      md: '1rem',        // 16px
      lg: '1.5rem',      // 24px
      xl: '2rem',        // 32px
      xxl: '3rem',       // 48px
      xxxl: '4rem',      // 64px
    },

    // Shape system - legacy CRM uses subtle rounded corners
    shape: {
      radiusNone: '0',
      radiusSm: '0.125rem',    // 2px
      radiusMd: '0.25rem',     // 4px - standard legacy CRM
      radiusLg: '0.25rem',     // 4px
      radiusXl: '0.5rem',      // 8px
      radius2xl: '0.5rem',
      radiusFull: '15rem',

      // Component specific - all match legacy CRM
      radiusButton: '0.25rem',
      radiusInput: '0.25rem',
      radiusCard: '0.25rem',
      radiusModal: '0.25rem',
      radiusDropdown: '0.25rem',
      radiusBadge: '0.25rem',
      radiusAvatar: '50%',
      radiusTab: '0',
    },

    // Border system — standard CRM layout
    borders: {
      widthThin: '1px',
      widthMedium: '2px',
      widthThick: '3px',

      styleCard: '1px solid #c9c9c9',
      styleInput: '1px solid #c9c9c9',
      styleFocus: '0 0 3px #0176d3',
      styleDivider: '1px solid #dddbda',
    },

    // Shadow system - Exact legacy CRM shadows
    shadows: {
      none: 'none',
      xs: '0 2px 2px 0 rgba(0, 0, 0, 0.1)',
      sm: '0 2px 2px 0 rgba(0, 0, 0, 0.1)',
      md: '0 2px 3px 0 rgba(0, 0, 0, 0.16)',
      lg: '0 0 8px rgba(0, 0, 0, 0.16)',
      xl: '0 2px 8px rgba(0, 0, 0, 0.16)',
      xxl: '0 2px 18px 0 rgba(0, 0, 0, 0.16)',

      // Component specific - legacy CRM card shadow
      card: '0 2px 2px 0 rgba(0, 0, 0, 0.1)',
      cardHover: '0 2px 3px 0 rgba(0, 0, 0, 0.16)',
      dropdown: '0 2px 3px 0 rgba(0, 0, 0, 0.16)',
      modal: '0 2px 18px 0 rgba(0, 0, 0, 0.16)',
      button: 'none',
      buttonHover: 'none',
      input: 'none',
      inputFocus: '0 0 3px #0176d3',
    },

    // Transitions - legacy CRM uses 0.1s
    transitions: {
      fast: '0.1s linear',
      normal: '0.1s linear',
      slow: '0.2s linear',
      bounce: '0.2s ease-out',
    },

    // Component specific styles - Exact legacy CRM sizing
    components: {
      // Header - legacy CRM global header
      headerHeight: '45px',
      headerPadding: '0 1rem',

      // Sidebar
      sidebarWidth: '16rem',
      sidebarCollapsedWidth: '3.5rem',

      // Cards
      cardPadding: '0.75rem 1rem',
      cardGap: '0.75rem',

      // Tables - legacy CRM table styling
      tableHeaderBg: '#fafaf9',
      tableRowHoverBg: '#f3f3f3',
      tableStripedBg: '#fafaf9',
      tableCellPadding: '0.5rem 0.75rem',

      // Inputs - legacy CRM 32px height
      inputHeight: '2rem',
      inputPadding: '0 0.75rem',

      // Buttons - legacy CRM 32px height
      buttonHeight: '2rem',
      buttonPadding: '0 1rem',
      buttonFontWeight: '400',

      // Badges
      badgeHeight: '1.25rem',
      badgePadding: '0 0.5rem',

      // Modal
      modalMaxWidth: '50rem',
    },
  },

  // Design 2: Obsidian Pro - Premium Dark Theme with Glassmorphism
  obsidian: {
    id: 'obsidian',
    name: 'Obsidian Pro',
    nameHe: 'אובסידיאן פרו',
    description: 'Professional dark theme with glass effects',
    descriptionHe: 'ערכת נושא כהה פרימיום עם אפקטים זכוכיתיים',
    icon: '⚫',
    isDark: true,

    colors: {
      primary: '#6366f1',
      primaryHover: '#818cf8',
      primaryLight: 'rgba(99, 102, 241, 0.15)',
      primaryDark: '#4f46e5',

      secondary: '#a1a1aa',
      secondaryHover: '#d4d4d8',

      accent: '#8b5cf6',
      accentLight: 'rgba(139, 92, 246, 0.15)',

      bgPrimary: '#09090b',
      bgSecondary: '#18181b',
      bgTertiary: '#27272a',
      bgHeader: 'rgba(9, 9, 11, 0.8)',
      bgSidebar: '#18181b',
      bgCard: '#18181b',
      bgHover: '#27272a',
      bgSelected: 'rgba(99, 102, 241, 0.2)',
      bgInput: '#27272a',
      bgModal: '#18181b',

      textPrimary: '#fafafa',
      textSecondary: '#a1a1aa',
      textTertiary: '#71717a',
      textInverse: '#09090b',
      textLink: '#818cf8',
      textMuted: '#52525b',

      borderPrimary: '#27272a',
      borderSecondary: '#3f3f46',
      borderFocus: '#6366f1',
      borderInput: '#3f3f46',

      success: '#22c55e',
      successLight: 'rgba(34, 197, 94, 0.15)',
      successDark: '#16a34a',
      warning: '#f59e0b',
      warningLight: 'rgba(245, 158, 11, 0.15)',
      warningDark: '#d97706',
      error: '#ef4444',
      errorLight: 'rgba(239, 68, 68, 0.15)',
      errorDark: '#dc2626',
      info: '#6366f1',
      infoLight: 'rgba(99, 102, 241, 0.15)',

      shadow: 'rgba(0, 0, 0, 0.5)',
      overlay: 'rgba(0, 0, 0, 0.8)',
      divider: '#27272a',

      // Special for glassmorphism
      glassBg: 'rgba(24, 24, 27, 0.7)',
      glassBlur: '12px',
      glassBorder: 'rgba(255, 255, 255, 0.1)',
    },

    typography: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      fontFamilyHeading: "'Inter', -apple-system, sans-serif",
      fontFamilyMono: "'JetBrains Mono', 'Fira Code', monospace",

      fontSizeXs: '11px',
      fontSizeSm: '12px',
      fontSizeMd: '14px',
      fontSizeLg: '16px',
      fontSizeXl: '20px',
      fontSize2xl: '24px',
      fontSize3xl: '30px',

      fontWeightLight: '300',
      fontWeightNormal: '400',
      fontWeightMedium: '500',
      fontWeightSemibold: '600',
      fontWeightBold: '700',

      lineHeightTight: '1.3',
      lineHeightNormal: '1.6',
      lineHeightRelaxed: '1.8',

      letterSpacingTight: '-0.02em',
      letterSpacingNormal: '-0.01em',
      letterSpacingWide: '0',
    },

    spacing: {
      unit: '8px',
      xxs: '2px',
      xs: '4px',
      sm: '8px',
      md: '16px',
      lg: '24px',
      xl: '32px',
      xxl: '48px',
      xxxl: '64px',
    },

    shape: {
      radiusNone: '0px',
      radiusSm: '6px',
      radiusMd: '8px',
      radiusLg: '12px',
      radiusXl: '16px',
      radius2xl: '20px',
      radiusFull: '9999px',

      radiusButton: '8px',
      radiusInput: '8px',
      radiusCard: '12px',
      radiusModal: '16px',
      radiusDropdown: '8px',
      radiusBadge: '6px',
      radiusAvatar: '12px',
      radiusTab: '8px 8px 0 0',
    },

    borders: {
      widthThin: '1px',
      widthMedium: '2px',
      widthThick: '3px',

      styleCard: '1px solid rgba(255, 255, 255, 0.1)',
      styleInput: '1px solid var(--color-border-input)',
      styleFocus: '2px solid var(--color-border-focus)',
      styleDivider: '1px solid var(--color-divider)',
    },

    shadows: {
      none: 'none',
      xs: '0 1px 2px rgba(0, 0, 0, 0.2)',
      sm: '0 2px 4px rgba(0, 0, 0, 0.3)',
      md: '0 4px 12px rgba(0, 0, 0, 0.4)',
      lg: '0 8px 24px rgba(0, 0, 0, 0.5)',
      xl: '0 16px 48px rgba(0, 0, 0, 0.6)',
      xxl: '0 24px 64px rgba(0, 0, 0, 0.7)',

      card: '0 4px 16px rgba(0, 0, 0, 0.4)',
      cardHover: '0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(99, 102, 241, 0.3)',
      dropdown: '0 8px 32px rgba(0, 0, 0, 0.5)',
      modal: '0 24px 64px rgba(0, 0, 0, 0.7)',
      button: '0 2px 8px rgba(0, 0, 0, 0.3)',
      buttonHover: '0 4px 16px rgba(99, 102, 241, 0.4)',
      input: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
      inputFocus: '0 0 0 3px rgba(99, 102, 241, 0.3)',

      // Glow effects
      glow: '0 0 20px rgba(99, 102, 241, 0.4)',
      glowSuccess: '0 0 20px rgba(34, 197, 94, 0.4)',
      glowError: '0 0 20px rgba(239, 68, 68, 0.4)',
    },

    transitions: {
      fast: '0.15s ease',
      normal: '0.25s ease',
      slow: '0.4s ease',
      bounce: '0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    },

    components: {
      headerHeight: '60px',
      headerPadding: '0 20px',

      sidebarWidth: '260px',
      sidebarCollapsedWidth: '64px',

      cardPadding: '20px',
      cardGap: '16px',

      tableHeaderBg: '#27272a',
      tableRowHoverBg: '#27272a',
      tableStripedBg: 'rgba(39, 39, 42, 0.5)',
      tableCellPadding: '14px 16px',

      inputHeight: '40px',
      inputPadding: '0 14px',

      buttonHeight: '40px',
      buttonPadding: '0 20px',
      buttonFontWeight: '500',

      badgeHeight: '22px',
      badgePadding: '0 10px',

      modalMaxWidth: '600px',
    },
  },

  // Design 3: Aurora Light - Clean, Spacious, Nature-Inspired
  aurora: {
    id: 'aurora',
    name: 'Aurora Light',
    nameHe: 'אורורה לייט',
    description: 'Clean, spacious design with calming nature tones',
    descriptionHe: 'עיצוב נקי ואוורירי בגווני טבע רגועים',
    icon: '🔵',

    colors: {
      primary: '#2563eb',
      primaryHover: '#1d4ed8',
      primaryLight: '#eff6ff',
      primaryDark: '#1e3a8a',

      secondary: '#64748b',
      secondaryHover: '#475569',

      accent: '#3b82f6',
      accentLight: '#dbeafe',

      bgPrimary: '#f8fafc',
      bgSecondary: '#ffffff',
      bgTertiary: '#f1f5f9',
      bgHeader: '#ffffff',
      bgSidebar: '#ffffff',
      bgCard: '#ffffff',
      bgHover: '#f1f5f9',
      bgSelected: '#eff6ff',
      bgInput: '#ffffff',
      bgModal: '#ffffff',

      textPrimary: '#0f172a',
      textSecondary: '#334155',
      textTertiary: '#64748b',
      textInverse: '#ffffff',
      textLink: '#2563eb',
      textMuted: '#94a3b8',

      borderPrimary: '#e2e8f0',
      borderSecondary: '#cbd5e1',
      borderFocus: '#2563eb',
      borderInput: '#e2e8f0',

      success: '#22c55e',
      successLight: '#f0fdf4',
      successDark: '#16a34a',
      warning: '#d97706',
      warningLight: '#fffbeb',
      warningDark: '#b45309',
      error: '#dc2626',
      errorLight: '#fef2f2',
      errorDark: '#b91c1c',
      info: '#0891b2',
      infoLight: '#ecfeff',

      shadow: 'rgba(15, 23, 42, 0.06)',
      overlay: 'rgba(15, 23, 42, 0.4)',
      divider: '#e2e8f0',
    },

    typography: {
      fontFamily: "'Plus Jakarta Sans', 'DM Sans', -apple-system, sans-serif",
      fontFamilyHeading: "'Plus Jakarta Sans', -apple-system, sans-serif",
      fontFamilyMono: "'Fira Code', 'Source Code Pro', monospace",

      fontSizeXs: '11px',
      fontSizeSm: '13px',
      fontSizeMd: '15px',
      fontSizeLg: '17px',
      fontSizeXl: '21px',
      fontSize2xl: '26px',
      fontSize3xl: '32px',

      fontWeightLight: '300',
      fontWeightNormal: '400',
      fontWeightMedium: '500',
      fontWeightSemibold: '600',
      fontWeightBold: '700',

      lineHeightTight: '1.3',
      lineHeightNormal: '1.6',
      lineHeightRelaxed: '1.8',

      letterSpacingTight: '-0.02em',
      letterSpacingNormal: '-0.01em',
      letterSpacingWide: '0.02em',
    },

    spacing: {
      unit: '8px',
      xxs: '2px',
      xs: '4px',
      sm: '10px',
      md: '18px',
      lg: '28px',
      xl: '40px',
      xxl: '56px',
      xxxl: '80px',
    },

    shape: {
      radiusNone: '0px',
      radiusSm: '8px',
      radiusMd: '12px',
      radiusLg: '16px',
      radiusXl: '20px',
      radius2xl: '24px',
      radiusFull: '9999px',

      radiusButton: '10px',
      radiusInput: '10px',
      radiusCard: '16px',
      radiusModal: '20px',
      radiusDropdown: '12px',
      radiusBadge: '8px',
      radiusAvatar: '14px',
      radiusTab: '10px 10px 0 0',
    },

    borders: {
      widthThin: '1px',
      widthMedium: '2px',
      widthThick: '3px',

      styleCard: '1px solid var(--color-border-primary)',
      styleInput: '1.5px solid var(--color-border-input)',
      styleFocus: '2px solid var(--color-border-focus)',
      styleDivider: '1px solid var(--color-divider)',
    },

    shadows: {
      none: 'none',
      xs: '0 1px 2px rgba(15, 23, 42, 0.03)',
      sm: '0 1px 3px rgba(15, 23, 42, 0.04), 0 1px 2px rgba(15, 23, 42, 0.02)',
      md: '0 4px 6px rgba(15, 23, 42, 0.04), 0 2px 4px rgba(15, 23, 42, 0.02)',
      lg: '0 10px 15px rgba(15, 23, 42, 0.05), 0 4px 6px rgba(15, 23, 42, 0.02)',
      xl: '0 20px 25px rgba(15, 23, 42, 0.06), 0 10px 10px rgba(15, 23, 42, 0.02)',
      xxl: '0 25px 50px rgba(15, 23, 42, 0.1)',

      card: '0 1px 3px rgba(15, 23, 42, 0.04), 0 1px 2px rgba(15, 23, 42, 0.02)',
      cardHover: '0 10px 25px rgba(15, 23, 42, 0.08), 0 4px 10px rgba(15, 23, 42, 0.03)',
      dropdown: '0 10px 40px rgba(15, 23, 42, 0.1)',
      modal: '0 25px 60px rgba(15, 23, 42, 0.15)',
      button: '0 1px 2px rgba(15, 23, 42, 0.04)',
      buttonHover: '0 4px 8px rgba(37, 99, 235, 0.2)',
      input: 'none',
      inputFocus: '0 0 0 4px rgba(37, 99, 235, 0.15)',
    },

    transitions: {
      fast: '0.15s ease-out',
      normal: '0.25s ease-out',
      slow: '0.4s ease-out',
      bounce: '0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
    },

    components: {
      headerHeight: '64px',
      headerPadding: '0 24px',

      sidebarWidth: '280px',
      sidebarCollapsedWidth: '72px',

      cardPadding: '24px',
      cardGap: '20px',

      tableHeaderBg: '#f8fafc',
      tableRowHoverBg: '#f1f5f9',
      tableStripedBg: '#f8fafc',
      tableCellPadding: '16px 20px',

      inputHeight: '44px',
      inputPadding: '0 16px',

      buttonHeight: '44px',
      buttonPadding: '0 24px',
      buttonFontWeight: '600',

      badgeHeight: '24px',
      badgePadding: '0 12px',

      modalMaxWidth: '560px',
    },
  },

  // Design 4: Nebula Enterprise - Dense, Professional, Data-Heavy
  nebula: {
    id: 'nebula',
    name: 'Nebula Enterprise',
    nameHe: 'נבולה אנטרפרייז',
    description: 'Dense professional design for data-heavy workflows',
    descriptionHe: 'עיצוב ארגוני צפוף לעבודה עם נתונים מרובים',
    icon: '🟣',

    colors: {
      primary: '#7c3aed',
      primaryHover: '#6d28d9',
      primaryLight: '#f5f3ff',
      primaryDark: '#5b21b6',

      secondary: '#64748b',
      secondaryHover: '#475569',

      accent: '#06b6d4',
      accentLight: '#ecfeff',

      bgPrimary: '#f8f9fa',
      bgSecondary: '#ffffff',
      bgTertiary: '#f1f3f5',
      bgHeader: '#1e1b4b',
      bgSidebar: '#1e1b4b',
      bgCard: '#ffffff',
      bgHover: '#f1f3f5',
      bgSelected: '#f5f3ff',
      bgInput: '#ffffff',
      bgModal: '#ffffff',

      textPrimary: '#1e293b',
      textSecondary: '#475569',
      textTertiary: '#64748b',
      textInverse: '#ffffff',
      textLink: '#7c3aed',
      textMuted: '#94a3b8',

      borderPrimary: '#e2e8f0',
      borderSecondary: '#cbd5e1',
      borderFocus: '#7c3aed',
      borderInput: '#d1d5db',

      success: '#16a34a',
      successLight: '#f0fdf4',
      successDark: '#15803d',
      warning: '#ea580c',
      warningLight: '#fff7ed',
      warningDark: '#c2410c',
      error: '#dc2626',
      errorLight: '#fef2f2',
      errorDark: '#b91c1c',
      info: '#7c3aed',
      infoLight: '#f5f3ff',

      shadow: 'rgba(30, 27, 75, 0.08)',
      overlay: 'rgba(30, 27, 75, 0.6)',
      divider: '#e2e8f0',
    },

    typography: {
      fontFamily: "'IBM Plex Sans', 'Segoe UI', system-ui, sans-serif",
      fontFamilyHeading: "'IBM Plex Sans', system-ui, sans-serif",
      fontFamilyMono: "'IBM Plex Mono', 'Consolas', monospace",

      fontSizeXs: '10px',
      fontSizeSm: '11px',
      fontSizeMd: '13px',
      fontSizeLg: '15px',
      fontSizeXl: '18px',
      fontSize2xl: '22px',
      fontSize3xl: '26px',

      fontWeightLight: '300',
      fontWeightNormal: '400',
      fontWeightMedium: '500',
      fontWeightSemibold: '600',
      fontWeightBold: '700',

      lineHeightTight: '1.2',
      lineHeightNormal: '1.4',
      lineHeightRelaxed: '1.6',

      letterSpacingTight: '0',
      letterSpacingNormal: '0.01em',
      letterSpacingWide: '0.04em',
    },

    spacing: {
      unit: '4px',
      xxs: '2px',
      xs: '4px',
      sm: '6px',
      md: '12px',
      lg: '16px',
      xl: '24px',
      xxl: '32px',
      xxxl: '48px',
    },

    shape: {
      radiusNone: '0px',
      radiusSm: '3px',
      radiusMd: '4px',
      radiusLg: '6px',
      radiusXl: '8px',
      radius2xl: '12px',
      radiusFull: '9999px',

      radiusButton: '4px',
      radiusInput: '4px',
      radiusCard: '6px',
      radiusModal: '8px',
      radiusDropdown: '4px',
      radiusBadge: '3px',
      radiusAvatar: '6px',
      radiusTab: '4px 4px 0 0',
    },

    borders: {
      widthThin: '1px',
      widthMedium: '2px',
      widthThick: '3px',

      styleCard: '1px solid var(--color-border-primary)',
      styleInput: '1px solid var(--color-border-input)',
      styleFocus: '2px solid var(--color-border-focus)',
      styleDivider: '1px solid var(--color-divider)',
    },

    shadows: {
      none: 'none',
      xs: '0 1px 2px rgba(30, 27, 75, 0.04)',
      sm: '0 1px 3px rgba(30, 27, 75, 0.06)',
      md: '0 2px 6px rgba(30, 27, 75, 0.08)',
      lg: '0 4px 12px rgba(30, 27, 75, 0.1)',
      xl: '0 8px 24px rgba(30, 27, 75, 0.12)',
      xxl: '0 12px 36px rgba(30, 27, 75, 0.15)',

      card: '0 1px 3px rgba(30, 27, 75, 0.06)',
      cardHover: '0 4px 12px rgba(30, 27, 75, 0.1)',
      dropdown: '0 4px 16px rgba(30, 27, 75, 0.12)',
      modal: '0 12px 40px rgba(30, 27, 75, 0.2)',
      button: '0 1px 2px rgba(30, 27, 75, 0.04)',
      buttonHover: '0 2px 6px rgba(124, 58, 237, 0.25)',
      input: 'inset 0 1px 2px rgba(30, 27, 75, 0.04)',
      inputFocus: '0 0 0 2px rgba(124, 58, 237, 0.2)',
    },

    transitions: {
      fast: '0.1s ease',
      normal: '0.15s ease',
      slow: '0.25s ease',
      bounce: '0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    },

    components: {
      headerHeight: '48px',
      headerPadding: '0 12px',

      sidebarWidth: '200px',
      sidebarCollapsedWidth: '48px',

      cardPadding: '12px',
      cardGap: '8px',

      tableHeaderBg: '#f8f9fa',
      tableRowHoverBg: '#f5f3ff',
      tableStripedBg: '#f8f9fa',
      tableCellPadding: '8px 12px',

      inputHeight: '32px',
      inputPadding: '0 10px',

      buttonHeight: '32px',
      buttonPadding: '0 14px',
      buttonFontWeight: '500',

      badgeHeight: '18px',
      badgePadding: '0 6px',

      modalMaxWidth: '720px',
    },
  },

  // Design 5: Fusion Minimal - Ultra-Minimalist, Editorial Style
  fusion: {
    id: 'fusion',
    name: 'Fusion Minimal',
    nameHe: 'פיוז\'ן מינימל',
    description: 'Ultra-minimal editorial style - less is more',
    descriptionHe: 'מינימליזם קיצוני בסגנון עיתונאי - פחות זה יותר',
    icon: '🔴',

    colors: {
      primary: '#171717',
      primaryHover: '#262626',
      primaryLight: '#f5f5f5',
      primaryDark: '#0a0a0a',

      secondary: '#737373',
      secondaryHover: '#525252',

      accent: '#dc2626',
      accentLight: '#fef2f2',

      bgPrimary: '#ffffff',
      bgSecondary: '#ffffff',
      bgTertiary: '#fafafa',
      bgHeader: '#ffffff',
      bgSidebar: '#ffffff',
      bgCard: '#ffffff',
      bgHover: '#fafafa',
      bgSelected: '#f5f5f5',
      bgInput: '#ffffff',
      bgModal: '#ffffff',

      textPrimary: '#0a0a0a',
      textSecondary: '#404040',
      textTertiary: '#737373',
      textInverse: '#ffffff',
      textLink: '#0a0a0a',
      textMuted: '#a3a3a3',

      borderPrimary: '#e5e5e5',
      borderSecondary: '#d4d4d4',
      borderFocus: '#0a0a0a',
      borderInput: '#d4d4d4',

      success: '#16a34a',
      successLight: '#f0fdf4',
      successDark: '#15803d',
      warning: '#ca8a04',
      warningLight: '#fefce8',
      warningDark: '#a16207',
      error: '#dc2626',
      errorLight: '#fef2f2',
      errorDark: '#b91c1c',
      info: '#171717',
      infoLight: '#f5f5f5',

      shadow: 'rgba(0, 0, 0, 0.03)',
      overlay: 'rgba(10, 10, 10, 0.5)',
      divider: '#e5e5e5',
    },

    typography: {
      fontFamily: "'Geist', 'Inter', -apple-system, system-ui, sans-serif",
      fontFamilyHeading: "'Geist', -apple-system, sans-serif",
      fontFamilyMono: "'Geist Mono', 'SF Mono', monospace",

      fontSizeXs: '10px',
      fontSizeSm: '12px',
      fontSizeMd: '14px',
      fontSizeLg: '16px',
      fontSizeXl: '20px',
      fontSize2xl: '28px',
      fontSize3xl: '36px',

      fontWeightLight: '300',
      fontWeightNormal: '400',
      fontWeightMedium: '450',
      fontWeightSemibold: '500',
      fontWeightBold: '600',

      lineHeightTight: '1.2',
      lineHeightNormal: '1.5',
      lineHeightRelaxed: '1.7',

      letterSpacingTight: '-0.03em',
      letterSpacingNormal: '-0.02em',
      letterSpacingWide: '0.1em',
    },

    spacing: {
      unit: '8px',
      xxs: '2px',
      xs: '4px',
      sm: '8px',
      md: '16px',
      lg: '24px',
      xl: '40px',
      xxl: '64px',
      xxxl: '96px',
    },

    shape: {
      radiusNone: '0px',
      radiusSm: '0px',
      radiusMd: '0px',
      radiusLg: '0px',
      radiusXl: '0px',
      radius2xl: '0px',
      radiusFull: '9999px',

      radiusButton: '0px',
      radiusInput: '0px',
      radiusCard: '0px',
      radiusModal: '0px',
      radiusDropdown: '0px',
      radiusBadge: '0px',
      radiusAvatar: '0px',
      radiusTab: '0px',
    },

    borders: {
      widthThin: '1px',
      widthMedium: '2px',
      widthThick: '3px',

      styleCard: 'none',
      styleInput: '0 0 1px var(--color-border-input)',
      styleFocus: '0 0 2px var(--color-border-focus)',
      styleDivider: '1px solid var(--color-divider)',
    },

    shadows: {
      none: 'none',
      xs: 'none',
      sm: 'none',
      md: 'none',
      lg: 'none',
      xl: 'none',
      xxl: '0 0 0 1px rgba(0, 0, 0, 0.05)',

      card: 'none',
      cardHover: 'none',
      dropdown: '0 4px 24px rgba(0, 0, 0, 0.1)',
      modal: '0 0 0 1px rgba(0, 0, 0, 0.1)',
      button: 'none',
      buttonHover: 'none',
      input: 'none',
      inputFocus: 'none',
    },

    transitions: {
      fast: '0.1s ease',
      normal: '0.15s ease',
      slow: '0.2s ease',
      bounce: '0.2s ease',
    },

    components: {
      headerHeight: '52px',
      headerPadding: '0 24px',

      sidebarWidth: '220px',
      sidebarCollapsedWidth: '52px',

      cardPadding: '20px',
      cardGap: '1px',

      tableHeaderBg: '#ffffff',
      tableRowHoverBg: '#fafafa',
      tableStripedBg: '#fafafa',
      tableCellPadding: '12px 16px',

      inputHeight: '40px',
      inputPadding: '0 0',

      buttonHeight: '40px',
      buttonPadding: '0 20px',
      buttonFontWeight: '500',

      badgeHeight: '20px',
      badgePadding: '0 8px',

      modalMaxWidth: '480px',
    },
  },
};

// Default theme
const DEFAULT_THEME = 'aurora';

// Create context
const ThemeContext = createContext(null);

/**
 * Convert theme object to CSS variables
 */
const themeToCssVariables = (theme) => {
  const vars = {};

  // Colors - flatten with kebab-case
  Object.entries(theme.colors).forEach(([key, value]) => {
    vars[`--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`] = value;
  });

  // Typography
  Object.entries(theme.typography).forEach(([key, value]) => {
    vars[`--font-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`] = value;
  });

  // Spacing
  Object.entries(theme.spacing).forEach(([key, value]) => {
    vars[`--space-${key}`] = value;
  });

  // Shape
  Object.entries(theme.shape).forEach(([key, value]) => {
    vars[`--radius-${key.replace(/([A-Z])/g, '-$1').toLowerCase().replace('radius-', '')}`] = value;
  });

  // Borders
  Object.entries(theme.borders).forEach(([key, value]) => {
    vars[`--border-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`] = value;
  });

  // Shadows
  Object.entries(theme.shadows).forEach(([key, value]) => {
    vars[`--shadow-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`] = value;
  });

  // Transitions
  Object.entries(theme.transitions).forEach(([key, value]) => {
    vars[`--transition-${key}`] = value;
  });

  // Components
  Object.entries(theme.components).forEach(([key, value]) => {
    vars[`--component-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`] = value;
  });

  return vars;
};

/**
 * Apply CSS variables to document root
 */
const applyCssVariables = (theme) => {
  const vars = themeToCssVariables(theme);
  const root = document.documentElement;

  Object.entries(vars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });

  // Add/remove dark mode class
  if (theme.isDark) {
    root.classList.add('theme-dark');
    root.classList.remove('theme-light');
  } else {
    root.classList.add('theme-light');
    root.classList.remove('theme-dark');
  }

  // Add theme ID as class for theme-specific overrides
  Object.keys(THEMES).forEach(id => {
    root.classList.remove(`theme-${id}`);
  });
  root.classList.add(`theme-${theme.id}`);
};

/**
 * ThemeProvider - Provides theme context throughout the app
 */
export const ThemeProvider = ({ children }) => {
  const [currentThemeId, setCurrentThemeId] = useState(DEFAULT_THEME);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewThemeId, setPreviewThemeId] = useState(null);

  // Get current theme object
  const currentTheme = useMemo(() => {
    const themeId = isPreviewMode && previewThemeId ? previewThemeId : currentThemeId;
    return THEMES[themeId] || THEMES[DEFAULT_THEME];
  }, [currentThemeId, isPreviewMode, previewThemeId]);

  // Load saved theme on mount
  useEffect(() => {
    const loadSavedTheme = () => {
      try {
        const saved = localStorage.getItem('afikim_theme');
        if (saved && THEMES[saved]) {
          setCurrentThemeId(saved);
        }
      } catch (err) {
        console.error('Error loading theme:', err);
      }
    };

    loadSavedTheme();
  }, []);

  // Apply CSS variables when theme changes
  useEffect(() => {
    applyCssVariables(currentTheme);
  }, [currentTheme]);

  // Set theme
  const setTheme = useCallback((themeId) => {
    if (!THEMES[themeId]) {
      console.warn(`Theme "${themeId}" not found`);
      return;
    }

    setCurrentThemeId(themeId);
    setIsPreviewMode(false);
    setPreviewThemeId(null);

    // Save to localStorage
    try {
      localStorage.setItem('afikim_theme', themeId);
    } catch (err) {
      console.error('Error saving theme:', err);
    }
  }, []);

  // Preview theme (temporary)
  const previewTheme = useCallback((themeId) => {
    if (!THEMES[themeId]) return;
    setIsPreviewMode(true);
    setPreviewThemeId(themeId);
  }, []);

  // Cancel preview
  const cancelPreview = useCallback(() => {
    setIsPreviewMode(false);
    setPreviewThemeId(null);
  }, []);

  // Apply previewed theme
  const applyPreview = useCallback(() => {
    if (previewThemeId) {
      setTheme(previewThemeId);
    }
  }, [previewThemeId, setTheme]);

  // Reset to default theme
  const resetTheme = useCallback(() => {
    setTheme(DEFAULT_THEME);
  }, [setTheme]);

  // Get all available themes
  const getThemes = useCallback(() => {
    return Object.values(THEMES);
  }, []);

  // Check if a theme is active
  const isThemeActive = useCallback((themeId) => {
    return currentThemeId === themeId;
  }, [currentThemeId]);

  const value = useMemo(() => ({
    // Current theme
    theme: currentTheme,
    themeId: currentThemeId,
    isDarkMode: currentTheme.isDark || false,

    // Preview state
    isPreviewMode,
    previewThemeId,

    // Actions
    setTheme,
    previewTheme,
    cancelPreview,
    applyPreview,
    resetTheme,

    // Utilities
    getThemes,
    isThemeActive,
    themes: THEMES,
  }), [
    currentTheme,
    currentThemeId,
    isPreviewMode,
    previewThemeId,
    setTheme,
    previewTheme,
    cancelPreview,
    applyPreview,
    resetTheme,
    getThemes,
    isThemeActive,
  ]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook to use theme
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    // Return default values if not wrapped in provider
    return {
      theme: THEMES[DEFAULT_THEME],
      themeId: DEFAULT_THEME,
      isDarkMode: false,
      isPreviewMode: false,
      previewThemeId: null,
      setTheme: () => {},
      previewTheme: () => {},
      cancelPreview: () => {},
      applyPreview: () => {},
      resetTheme: () => {},
      getThemes: () => Object.values(THEMES),
      isThemeActive: () => false,
      themes: THEMES,
    };
  }
  return context;
};

export default ThemeContext;
